import {Injectable} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {DialogWidth, ThemePalette, constants} from '@q9elements/ui-kit/common';
import {ConfirmModalDialogComponent} from '@q9elements/ui-kit/components';

import {CreateProposedNodeDialogComponent} from '../components/create-proposed-node-dialog/create-proposed-node-dialog.component';
import {CreateNewViewDialogComponent} from '../components/custom-metadata-view/dialogs/components/create-new-view-dialog/create-new-view-dialog.component';
import {EditCustomViewColumnsDialogComponent} from '../components/custom-metadata-view/dialogs/components/edit-custom-view-columns-dialog/edit-custom-view-columns-dialog.component';
import {EditCustomViewDialogComponent} from '../components/custom-metadata-view/dialogs/components/edit-custom-view-dialog/edit-custom-view-dialog.component';
import {EditFiltersDialogComponent} from '../components/custom-metadata-view/dialogs/components/edit-filters-dialog/edit-filters-dialog.component';
import {EditSortDialogComponent} from '../components/custom-metadata-view/dialogs/components/edit-sort-dialog/edit-sort-dialog.component';
import {ICustomView} from '../models/custom-view.interface';
import {CreateProposedNodeData} from '../models/proposed-node.interface';

@Injectable({
  providedIn: 'root'
})
export class EditorInterfaceDialogService {
  constructor(
    public dialog: MatDialog,
    private translate: TranslateService
  ) {}

  getNodeLimitDialog(node: any): MatDialogRef<ConfirmModalDialogComponent> {
    const nodeName = node ? node.name || node.customId : 'node';

    const dialogData = {
      color: ThemePalette.PRIMARY,
      modalTitle: 'CONFIRM_DIALOG.TITLE_NODE_LIMIT',
      content: this.translate.instant('CONFIRM_DIALOG.TEXT_NODE_LIMIT', {nodeName}),
      confirmText: 'CONFIRM_DIALOG.CONFIRM_NODE_LIMIT',
      cancelText: 'CONFIRM_DIALOG.CANCEL_NODE_LIMIT',
      showCancelButton: true
    };
    return this.dialog.open(ConfirmModalDialogComponent, {
      width: DialogWidth.SM,
      data: dialogData
    });
  }

  /** Show this message when user create new node and this node will not be visible in current view */
  getGoToNewNodeDialog(node: any): MatDialogRef<ConfirmModalDialogComponent> {
    const dialogData = {
      color: ThemePalette.PRIMARY,
      modalTitle: 'CONFIRM_DIALOG.TITLE_GO_TO_NEW_NODE',
      content: this.translate.instant('CONFIRM_DIALOG.TEXT_GO_TO_NEW_NODE', {
        nodeName: node.name || node.customId
      }),
      showCancelButton: true,
      confirmText: 'CONFIRM_DIALOG.CONFIRM_GO_TO_NEW_NODE',
      cancelText: 'CONFIRM_DIALOG.CANCEL_GO_TO_NEW_NODE'
    };

    return this.dialog.open(ConfirmModalDialogComponent, {
      width: DialogWidth.SM,
      data: dialogData
    });
  }

  createProposedNodeDialog(data) {
    return this.dialog.open<CreateProposedNodeDialogComponent, CreateProposedNodeData>(
      CreateProposedNodeDialogComponent,
      {
        width: constants.dialog.width.md,
        panelClass: ['q9-dialog'],
        data
      }
    );
  }

  public openCreateNewViewDialog(
    refModelId: string,
    {allowGlobalViewCreate, allowPrivateViewCreate}
  ): MatDialogRef<CreateNewViewDialogComponent> {
    return this.dialog.open(CreateNewViewDialogComponent, {
      width: DialogWidth.LG_XL,
      panelClass: ['q9-dialog'],
      disableClose: true,
      data: {
        refModelId,
        allowGlobalViewCreate,
        allowPrivateViewCreate
      }
    });
  }

  public openEditCustomViewMetadataDialog(
    viewId: string
  ): MatDialogRef<EditCustomViewDialogComponent> {
    return this.dialog.open(EditCustomViewDialogComponent, {
      width: DialogWidth.LG_XL,
      panelClass: ['q9-dialog'],
      disableClose: true,
      data: {
        viewId
      }
    });
  }

  public openEditCustomViewColumnsDialog(payload: {
    viewId?: string;
    view?: ICustomView;
  }): MatDialogRef<EditCustomViewColumnsDialogComponent> {
    return this.dialog.open(EditCustomViewColumnsDialogComponent, {
      width: DialogWidth.LG_XL,
      panelClass: ['q9-dialog'],
      disableClose: true,
      data: payload
    });
  }

  public openEditFiltersDialog(payload: {
    viewId?: string;
    view?: ICustomView;
  }): MatDialogRef<EditFiltersDialogComponent> {
    return this.dialog.open(EditFiltersDialogComponent, {
      width: DialogWidth.LG_XL,
      panelClass: ['q9-dialog'],
      disableClose: true,
      data: payload
    });
  }

  public openEditSortDialog(payload: {
    viewId?: string;
    view?: ICustomView;
  }): MatDialogRef<EditSortDialogComponent> {
    return this.dialog.open(EditSortDialogComponent, {
      width: DialogWidth.MD,
      panelClass: ['q9-dialog'],
      disableClose: true,
      data: payload
    });
  }
  public openDeleteDialog(entityName: string): MatDialogRef<ConfirmModalDialogComponent> {
    return this.dialog.open(ConfirmModalDialogComponent, {
      width: DialogWidth.SM,
      data: {
        color: ThemePalette.WARN,
        modalTitle: this.translate.instant('RUBIC_CUBE.REMOVE_CUSTOM_VIEW_TITLE'),
        content: this.translate.instant('RUBIC_CUBE.REMOVE_CUSTOM_VIEW_TEXT', {name: entityName}),
        confirmText: this.translate.instant('RUBIC_CUBE.DELETE_BTN'),
        cancelText: this.translate.instant('GENERAL.CANCEL_BTN'),
        showCancelButton: true
      }
    });
  }
}
